import { EmptyState, Skeleton, useErrorToast } from '@h2hspark/ui';
import { UserPlus } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { PER_EVENT_LOCKED } from '../event-registrations/constants';
import { PerEventShell } from '../event-registrations/per-event-page';

import { useMyConnectionRequests } from './api';
import { ConnectionRequestCard } from './connection-request-card';
import {
  NO_EVENT_REQUESTS_DESCRIPTION,
  NO_EVENT_REQUESTS_TITLE,
  REQUESTS_TITLE,
  REQUEST_ACTION_FAILED,
  eventRequestsSubhead,
  mostRecentRequestFirst,
} from './constants';

/**
 * **One event's connection requests** — `Web App.dc.html` §2.5, `showRequests`, lines 483–513
 * (`MEM-032` AC-6, AC-7; `MEM-053` AC-1, AC-6).
 *
 * ## Why this page exists beside the global one
 *
 * The event header card carries a Requests control with a count, and the count is **this event's**
 * (`MEM-053` AC-6). Before this page, that control opened `/app/connections/requests`, the whole
 * queue — so a member who tapped a badge reading *2* arrived at a list of six, and the number they
 * had just read was wrong where they landed. The prototype's own answer is a per-event page, and
 * this is it: the same queue, the same cards, filtered to one evening.
 *
 * **The global screen is unchanged and is still what Connections opens.** `MEM-032` AC-6 names it
 * — *"reached from the connections list and from the in-app notification"* — and neither door
 * moved. This is a third door onto the same rows, which adds no behaviour: accepting here and
 * accepting there call one mutation and invalidate one cache.
 *
 * ## The stepper marks nothing
 *
 * §2.5 draws the card with its stepper and **no part active**, because connection requests are not
 * one of the three phases: they arrive before an event, during it and after it. `PerEventShell`
 * passes `requests`, which `ACTIVE_PHASE` maps to no phase at all.
 */
export function EventConnectionRequestsPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();

  return (
    <PerEventShell eventId={eventId} surface="requests" locked={PER_EVENT_LOCKED.requests}>
      {({ event }) => <EventRequestsBody eventId={eventId} eventName={event.name} />}
    </PerEventShell>
  );
}

function EventRequestsBody({
  eventId,
  eventName,
}: {
  readonly eventId: string;
  readonly eventName: string;
}): React.ReactElement {
  const query = useMyConnectionRequests({ enabled: true });
  const showError = useErrorToast();

  // The answer mutation lives on the card, as it does on the global screen; its failure toast is
  // raised here so one screen says one thing however many cards are on it.
  React.useEffect(() => {
    if (query.error !== null) showError(query.error, REQUEST_ACTION_FAILED);
  }, [query.error, showError]);

  /* **Filtered on the client, from the member's own inbound requests.** `GET
     /api/connections/requests` already answers only this member's pending inbound rows and each
     carries the event it came from, which is exactly what the header card counts — so this page and
     that badge read one list and cannot disagree. A per-event endpoint would be a second source of
     the same number. */
  const requests = mostRecentRequestFirst(
    (query.data?.requests ?? []).filter((request) => request.eventId === eventId),
  );

  return (
    <div className="space-y-7">
      <header>
        <h2 className="mb-1.5 text-2xl tracking-tight md:text-3xl">{REQUESTS_TITLE}</h2>
        <p className="max-w-[600px] text-md text-muted-foreground md:text-lg">
          {eventRequestsSubhead(eventName)}
        </p>
      </header>

      {query.isPending ? (
        <div className="space-y-3.5">
          <Skeleton className="h-[170px] w-full rounded-lg" />
          <Skeleton className="h-[170px] w-full rounded-lg" />
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<UserPlus className="h-6 w-6" aria-hidden />}
          title={NO_EVENT_REQUESTS_TITLE}
          description={NO_EVENT_REQUESTS_DESCRIPTION}
        />
      ) : (
        /* §2.5: `flex column; gap:14px`. One column at every width — a request is a paragraph of
           somebody's words and a decision, and two of those side by side invites skimming. */
        <ul className="flex flex-col gap-3.5">
          {requests.map((request) => (
            <li key={request.requesterRef}>
              <ConnectionRequestCard request={request} showEventPill={false} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
